import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Building2, Loader2, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/ChartCard';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/projects');
      setProjects(r.data || []);
    } catch (_e) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreated = (p) => {
    setProjects([p, ...projects]);
    setOpen(false);
    toast.success('Project created');
    navigate(`/app/projects/${p.id}`);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this project and all its data?')) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      toast.success('Project deleted');
    } catch (_e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Each project tracks visibility for one brand.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="projects-new-button"><Plus className="h-4 w-4 mr-1.5" /> New project</Button>
          </DialogTrigger>
          <CreateProjectDialog onCreated={onCreated} onCancel={() => setOpen(false)} />
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-[var(--radius)]" />)}
        </div>
      ) : projects.length === 0 ? (
        <Card className="border card-elevated">
          <CardContent className="py-12">
            <EmptyState
              title="No projects yet"
              description="Create your first brand project to run AI visibility scans."
              action={<Button onClick={() => setOpen(true)} data-testid="projects-empty-create-button"><Plus className="h-4 w-4 mr-1.5" /> Create project</Button>}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card key={p.id} className="border card-elevated card-elevated-hover transition-shadow flex flex-col" data-testid={`project-card-${p.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))]">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold">{p.name}</CardTitle>
                      <div className="text-xs text-muted-foreground">{p.domain || p.industry || '—'}</div>
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-rose-600" title="Delete" data-testid={`project-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between gap-3">
                <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">{p.description || 'No description added yet.'}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(p.competitors || []).slice(0, 3).map((c) => (
                    <Badge key={c} variant="secondary" className="text-[11px]">vs {c}</Badge>
                  ))}
                  {(p.competitors || []).length > 3 && (
                    <Badge variant="outline" className="text-[11px]">+{p.competitors.length - 3}</Badge>
                  )}
                </div>
                <div className="pt-1">
                  <Button variant="secondary" className="w-full" asChild data-testid={`project-open-${p.id}`}>
                    <Link to={`/app/projects/${p.id}`}>Open project</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateProjectDialog({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [industry, setIndustry] = useState('');
  const [description, setDescription] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [keywords, setKeywords] = useState('');
  const [brandDomains, setBrandDomains] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/projects', {
        name: name.trim(),
        domain: domain.trim(),
        industry: industry.trim(),
        description: description.trim(),
        competitors: competitors.split(',').map((c) => c.trim()).filter(Boolean),
        keywords: keywords.split(',').map((k) => k.trim()).filter(Boolean),
        brand_domains: brandDomains.split(',').map((d) => d.trim()).filter(Boolean),
      });
      onCreated(res.data);
    } catch (err) {
      // 402 (plan gate) already shows a rich Upgrade toast via the interceptor.
      if (err?.response?.status !== 402) {
        toast.error(errMsg(err, 'Failed to create project'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>New brand project</DialogTitle>
        <DialogDescription>Tell us about your brand so we can track its AI visibility accurately.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="pname">Brand name *</Label>
          <Input id="pname" required value={name} onChange={(e) => setName(e.target.value)} data-testid="project-create-name-input" placeholder="e.g. Notion" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pdomain">Domain</Label>
            <Input id="pdomain" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="project-create-domain-input" placeholder="notion.so" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pindustry">Industry</Label>
            <Input id="pindustry" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="project-create-industry-input" placeholder="Productivity software" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pbdoms">Brand domains (comma separated)</Label>
          <Input
            id="pbdoms"
            value={brandDomains}
            onChange={(e) => setBrandDomains(e.target.value)}
            data-testid="project-create-brand-domains-input"
            placeholder="notion.so, help.notion.so, blog.notion.so"
          />
          <p className="text-[11px] text-muted-foreground">Citations to these domains will count as your brand.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pdesc">Description</Label>
          <Textarea id="pdesc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="project-create-description-input" placeholder="What does your brand do?" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pcomp">Competitors (comma separated)</Label>
          <Input id="pcomp" value={competitors} onChange={(e) => setCompetitors(e.target.value)} data-testid="project-create-competitors-input" placeholder="Evernote, Obsidian, Coda" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pkw">Keywords (comma separated)</Label>
          <Input id="pkw" value={keywords} onChange={(e) => setKeywords(e.target.value)} data-testid="project-create-keywords-input" placeholder="note taking, wiki, project management" />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="project-create-submit-button">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create project
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
